/** Carried positional baskets (docs/positional.md phase E).
 *
 * Shows every OPEN multi-leg basket — the positions that deliberately
 * survive the daily square-off — as BasketTiles (legs, net entry cost,
 * latest mark, expiry, Force-exit). Renders nothing while no basket is
 * open, so the dashboard stays clean for intraday-only days. Paper-only
 * feed until live enablement. */

import type { ReactElement } from "react";
import { useQuery } from "@tanstack/react-query";
import { BasketTile } from "@/components/Positional/BasketTile";
import { tradeLogApi } from "@/lib/tradeLogApi";
import { ALL_STRATEGIES } from "@/types/tradeLog";

const BASKETS_KEY = ["carried-baskets"] as const;
const POLL_MS = 60_000;

export function CarriedBasketsCard(): ReactElement | null {
  const query = useQuery({
    queryKey: BASKETS_KEY,
    queryFn: () =>
      tradeLogApi.list({
        range: "today",
        strategyId: ALL_STRATEGIES,
        show: "trades",
        executionMode: "PAPER",
      }),
    refetchInterval: POLL_MS,
  });
  const baskets = (query.data?.trades ?? []).filter(
    (t) => t.horizon === "POSITIONAL" && t.exitReason === "OPEN",
  );
  if (baskets.length === 0) return null;
  return (
    <section className="card mb-3" data-testid="carried-baskets">
      <div className="mb-3 flex items-center gap-2">
        <i className="ti ti-calendar-week text-lg text-accent" />
        <h3>Carried baskets</h3>
        <span className="pill bg-surface-2 text-ink-secondary">{String(baskets.length)}</span>
      </div>
      <div className="grid gap-3 lg:grid-cols-2">
        {baskets.map((b) => (
          <BasketTile key={b.id} basket={b} />
        ))}
      </div>
    </section>
  );
}
